/**
 * Iter 145x/y — Halftime Daily Betslip panel.
 *
 * Renders the platform's 25-pick HT-only slate with league-reputation +
 * team-tendency intel notes overlaid on top of the model's confidence.
 *
 * Iter 145y additions:
 *  - 7-day strip lets users jump between today + upcoming days.
 *  - WhatsApp opt-in widget pipes users into the 06:50 UTC daily blast.
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { API } from "../App";
import {
  Crown, ShieldCheck, Sparkles, Lock, RefreshCcw,
  CheckCircle2, XCircle, Clock as ClockIcon, MessageCircle, Send,
  ChevronRight, MinusCircle, CalendarDays,
} from "lucide-react";
import { toast } from "sonner";
import { countryToFlag } from "../lib/countryFlag";
import MatchAnalysisModal from "./MatchAnalysisModal";
import QuantumCompareBadge, { useQuantumComparison } from "./QuantumCompareBadge";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Calendar } from "./ui/calendar";

const fmtKick = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "—";
  }
};

const fmtDayLabel = (iso, todayIso) => {
  if (!iso) return "—";
  if (iso === todayIso) return "Today";
  try {
    const today = new Date(todayIso + "T00:00:00Z");
    const d = new Date(iso + "T00:00:00Z");
    const dayMs = 24 * 60 * 60 * 1000;
    const delta = Math.round((d - today) / dayMs);
    if (delta === -1) return "Yesterday";
    return d.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" });
  } catch {
    return iso;
  }
};

const marketBadgeTone = (m) => {
  if ((m || "").startsWith("HT BTTS")) return "bg-amber-500/15 text-amber-200 border-amber-500/40";
  if ((m || "").startsWith("HT O/U 0.5")) return "bg-emerald-500/15 text-emerald-200 border-emerald-500/40";
  if ((m || "").startsWith("HT O/U 1.5")) return "bg-sky-500/15 text-sky-200 border-sky-500/40";
  return "bg-violet-500/15 text-violet-200 border-violet-500/40";
};

const outcomeChip = (outcome) => {
  if (outcome === "hit") return { Icon: CheckCircle2, cls: "text-emerald-300 bg-emerald-500/15 border-emerald-500/40", text: "HIT" };
  if (outcome === "miss") return { Icon: XCircle, cls: "text-rose-300 bg-rose-500/15 border-rose-500/40", text: "MISS" };
  if (outcome === "void") return { Icon: MinusCircle, cls: "text-slate-300 bg-slate-500/15 border-slate-500/40", text: "VOID" };
  return { Icon: ClockIcon, cls: "text-muted-foreground bg-foreground/5 border-border", text: "PENDING" };
};

function WhatsAppOptIn() {
  const [phone, setPhone] = useState(() => {
    try { return localStorage.getItem("ndaku_whatsapp_phone") || ""; }
    catch { return ""; }
  });
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null);  // {subscribed, active, subscribed_at, last_broadcast_at}

  const isValid = (p) => /^\+[1-9]\d{6,14}$/.test(p);

  const checkStatus = useCallback(async (p) => {
    const target = (p ?? phone).trim();
    if (!isValid(target)) return;
    try {
      const { data } = await axios.post(`${API}/whatsapp/status`, { phone: target });
      setStatus(data);
    } catch {
      /* non-fatal */
    }
  }, [phone]);

  // Auto-check on first mount if we have a remembered phone.
  useEffect(() => {
    if (phone && isValid(phone)) checkStatus(phone);
    // eslint-disable-next-line
  }, []);

  const subscribe = async () => {
    const trimmed = phone.trim();
    if (!isValid(trimmed)) {
      toast.error("Use E.164 format e.g. +254712345678");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/whatsapp/subscribe`, { phone: trimmed });
      try { localStorage.setItem("ndaku_whatsapp_phone", trimmed); } catch {}
      toast.success("Subscribed — Daily 25 (06:50 UTC) + Easy Double (07:05 UTC) incoming");
      await checkStatus(trimmed);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Subscription failed");
    } finally {
      setBusy(false);
    }
  };

  const unsubscribe = async () => {
    const trimmed = phone.trim();
    if (!isValid(trimmed)) {
      toast.error("Use E.164 format e.g. +254712345678");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/whatsapp/unsubscribe`, { phone: trimmed });
      toast.success("Unsubscribed");
      await checkStatus(trimmed);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unsubscribe failed");
    } finally {
      setBusy(false);
    }
  };

  const fmtRelative = (iso) => {
    if (!iso) return "—";
    try {
      const ms = Date.now() - new Date(iso).getTime();
      const mins = Math.round(ms / 60000);
      if (mins < 1) return "just now";
      if (mins < 60) return `${mins}m ago`;
      const hrs = Math.round(mins / 60);
      if (hrs < 24) return `${hrs}h ago`;
      return `${Math.round(hrs / 24)}d ago`;
    } catch {
      return "—";
    }
  };

  // Render badges for known state.
  const isActive = status?.active === true;
  const isOptedOut = status?.subscribed === true && status?.active === false;

  return (
    <div
      className="relative rounded-2xl border border-emerald-500/30 bg-gradient-to-br from-emerald-500/10 via-background to-background p-4 sm:p-5 mb-6"
      data-testid="ht-whatsapp-optin"
    >
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <div className="h-9 w-9 rounded-xl bg-emerald-500/20 grid place-items-center shrink-0">
            <MessageCircle className="w-4.5 h-4.5 text-emerald-300" />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-300 mb-0.5">
              WhatsApp Daily Drop
            </div>
            <h3 className="font-display font-bold text-base sm:text-lg leading-tight">
              25 HT picks at 06:50 + Easy Double at 07:05 UTC.
            </h3>
            <p className="text-[11px] sm:text-xs text-muted-foreground mt-1 leading-snug">
              One subscription, two drops — the Halftime Daily 25 plus the Safe 2.00× accumulator. Send <span className="font-mono text-foreground">join &lt;sandbox-code&gt;</span> to <span className="font-mono text-foreground">+1 415 523 8886</span> on WhatsApp first to opt-in.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0 w-full sm:w-auto">
          <input
            type="tel"
            inputMode="tel"
            placeholder="+254712345678"
            value={phone}
            onChange={(e) => { setPhone(e.target.value); setStatus(null); }}
            data-testid="ht-whatsapp-phone-input"
            className="flex-1 sm:w-44 h-10 rounded-lg border border-border bg-card px-3 text-sm font-mono tabular-nums focus:outline-none focus:border-emerald-500"
          />
          <button
            onClick={() => checkStatus()}
            disabled={busy || !isValid(phone)}
            data-testid="ht-whatsapp-check-btn"
            className="h-10 text-[10px] font-semibold text-muted-foreground hover:text-foreground uppercase tracking-wider px-2 disabled:opacity-30"
            title="Check status"
          >
            Check
          </button>
          <button
            onClick={subscribe}
            disabled={busy}
            data-testid="ht-whatsapp-subscribe-btn"
            className="h-10 inline-flex items-center gap-1.5 px-3.5 rounded-lg bg-emerald-500 text-emerald-950 font-bold text-xs uppercase tracking-wider hover:bg-emerald-400 transition-colors disabled:opacity-50"
          >
            <Send className="w-3.5 h-3.5" />
            Subscribe
          </button>
          <button
            onClick={unsubscribe}
            disabled={busy}
            data-testid="ht-whatsapp-unsubscribe-btn"
            className="h-10 text-[10px] font-semibold text-muted-foreground hover:text-foreground uppercase tracking-wider px-1"
            title="Unsubscribe"
          >
            Stop
          </button>
        </div>
      </div>

      {/* Subscription state badge */}
      {status && (
        <div
          className="mt-3 pt-3 border-t border-emerald-500/20 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[11px]"
          data-testid="ht-whatsapp-status"
        >
          {isActive ? (
            <span className="inline-flex items-center gap-1.5 text-emerald-300 font-semibold">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
              </span>
              Subscribed · Daily 25 + Easy Double active
            </span>
          ) : isOptedOut ? (
            <span className="inline-flex items-center gap-1.5 text-muted-foreground font-semibold">
              <span className="inline-block h-2 w-2 rounded-full bg-muted-foreground/50" />
              Opted out · subscribe to re-enable
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-muted-foreground font-semibold">
              <span className="inline-block h-2 w-2 rounded-full bg-muted-foreground/30" />
              Not subscribed yet
            </span>
          )}
          {status?.subscribed_at && (
            <span className="text-muted-foreground">
              Joined <span className="text-foreground/80">{fmtRelative(status.subscribed_at)}</span>
            </span>
          )}
          {status?.last_broadcast_at && (
            <span className="text-muted-foreground" data-testid="ht-whatsapp-last-25">
              Last 25 <span className="text-foreground/80">{fmtRelative(status.last_broadcast_at)}</span>
            </span>
          )}
          {status?.last_double_broadcast_at && (
            <span className="text-muted-foreground" data-testid="ht-whatsapp-last-double">
              Last Easy Double <span className="text-foreground/80">{fmtRelative(status.last_double_broadcast_at)}</span>
            </span>
          )}
        </div>
      )}
    </div>
  );
}

export default function HalftimeBetslipPanel() {
  const [data, setData] = useState(null);
  const [week, setWeek] = useState({ days: [], today: null });
  const [selectedDate, setSelectedDate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [calOpen, setCalOpen] = useState(false);

  // Iter 145ag — Click-to-analyze: open the platform's MatchAnalysisModal
  // for any pick, so users see the same deep-dive analysis the rest of
  // the site exposes.  Halftime is the platform's best-calibrated
  // market, so it deserves first-class drill-down.
  const [analysisOpen, setAnalysisOpen] = useState(false);
  const [analysisData, setAnalysisData] = useState(null);
  const [analysisLoadingFid, setAnalysisLoadingFid] = useState(null);
  const { i18n } = useTranslation();

  const openAnalysis = useCallback(async (fixtureId) => {
    if (!fixtureId) return;
    setAnalysisLoadingFid(fixtureId);
    try {
      const lang = (i18n?.language || 'en').split('-')[0];
      const { data } = await axios.get(
        `${API}/predictions/${fixtureId}/analysis`,
        { timeout: 30000, params: { lang } }
      );
      setAnalysisData(data);
      setAnalysisOpen(true);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 404) {
        toast.error("Match data was wiped by the daily sync — analysis unavailable for this fixture.");
      } else {
        toast.error("Failed to load match analysis");
      }
    } finally {
      setAnalysisLoadingFid(null);
    }
  }, [i18n]);

  // Iter 275f — Widened from 7 → 14 days so the horizontally-scrollable
  // week strip surfaces the previous ~12 days of hit-rate history
  // (was: yesterday + today + 5 future; now: last 12 + today + tomorrow).
  // Users complained they couldn't see any archived hit tracker beyond
  // yesterday.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/halftime-betslip/week?days=30`);
        setWeek(data);
        if (!selectedDate) setSelectedDate(data.today);
      } catch {
        // Non-fatal — the picks endpoint still renders today's slate.
      }
    })();
  }, []);  // eslint-disable-line

  const load = useCallback(async (date) => {
    setLoading(true);
    try {
      const url = date
        ? `${API}/halftime-betslip/today?date=${date}`
        : `${API}/halftime-betslip/today`;
      const { data } = await axios.get(url);
      setData(data);
    } catch (e) {
      toast.error("Failed to load halftime betslip");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedDate) load(selectedDate);
  }, [selectedDate, load]);

  const onRefresh = async () => {
    setRefreshing(true);
    try {
      await load(selectedDate);
    } finally {
      setRefreshing(false);
    }
  };

  const summary = data?.summary;
  const picks = data?.picks || [];

  // Iter 145bh — Side-by-side Quantum comparison badge for every HT pick.
  const quantumItems = picks.map((p) => ({
    fixture_id: p.fixture_id,
    market_type: p.pick?.market_short,
  })).filter((i) => i.fixture_id && i.market_type);
  const quantumLookup = useQuantumComparison(quantumItems);

  return (
    <section
      className="relative overflow-hidden rounded-3xl border border-amber-500/40 bg-gradient-to-br from-amber-500/10 via-background to-background p-5 sm:p-8 mb-8"
      data-testid="halftime-betslip-panel"
    >
      {/* Decorative gold flecks */}
      <div className="pointer-events-none absolute inset-0 opacity-20" aria-hidden>
        <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-amber-400/30 blur-3xl" />
        <div className="absolute -bottom-32 -left-16 h-72 w-72 rounded-full bg-amber-500/20 blur-3xl" />
      </div>

      <header className="relative flex items-start justify-between gap-4 mb-5 sm:mb-6">
        <div className="flex items-start gap-3 min-w-0">
          <div className="h-11 w-11 rounded-2xl bg-gradient-to-br from-amber-300 via-amber-400 to-amber-600 grid place-items-center shrink-0 shadow-lg shadow-amber-500/30">
            <Crown className="w-5.5 h-5.5 text-amber-950" strokeWidth={2.5} />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-[10px] uppercase tracking-[0.22em] font-extrabold text-amber-300/90">
                Halftime Intelligence
              </span>
              <span className="hidden sm:inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] font-bold text-foreground/70 bg-foreground/5 border border-border rounded-full px-2 py-0.5">
                <Lock className="w-2.5 h-2.5" /> Top secret
              </span>
            </div>
            <h2 className="font-display font-extrabold tracking-tight text-2xl sm:text-3xl truncate">
              Daily 25 · HT Betslip
            </h2>
            <p className="text-xs sm:text-sm text-muted-foreground mt-1.5 leading-relaxed max-w-2xl">
              25 highest-conviction halftime picks built from league HT-goal patterns + team first-half tendencies, layered on top of the model's math.
            </p>
          </div>
        </div>
        <div className="shrink-0 flex items-center gap-2">
          <Popover open={calOpen} onOpenChange={setCalOpen}>
            <PopoverTrigger asChild>
              <button
                data-testid="ht-betslip-date-picker-btn"
                className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border border-border bg-card hover:bg-foreground hover:text-background transition-colors min-touch sm:min-h-0 sm:min-w-0"
                title="Pick any past date"
              >
                <CalendarDays className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Date</span>
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-auto p-0" data-testid="ht-betslip-date-picker">
              <Calendar
                mode="single"
                selected={selectedDate ? new Date(selectedDate + "T00:00:00") : undefined}
                onSelect={(d) => {
                  if (!d) return;
                  const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
                  setSelectedDate(iso);
                  setCalOpen(false);
                }}
                // Allow any past date all the way back; cap at +7 days forward
                // since predictions are only generated ~1 day ahead.
                disabled={(d) => {
                  const max = new Date();
                  max.setDate(max.getDate() + 7);
                  return d > max;
                }}
                initialFocus
              />
            </PopoverContent>
          </Popover>
          <button
            onClick={onRefresh}
            disabled={refreshing || loading}
            data-testid="ht-betslip-refresh"
            className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border border-border bg-card hover:bg-foreground hover:text-background transition-colors disabled:opacity-50 min-touch sm:min-h-0 sm:min-w-0"
            title="Refresh"
          >
            <RefreshCcw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
            <span className="hidden sm:inline">Refresh</span>
          </button>
        </div>
      </header>

      {/* Week strip — today + next 6 days */}
      {week.days.length > 0 && (
        <div
          className="relative -mx-1 mb-6 flex items-stretch gap-2 overflow-x-auto pb-2 snap-x snap-mandatory scrollbar-thin"
          data-testid="ht-betslip-week-strip"
        >
          {week.days.map((d) => {
            const isActive = d.date === selectedDate;
            const hitRate = d.summary?.hit_rate;
            const isEmpty = (d.n_picks ?? 0) === 0;
            const fixtures = d.fixtures_scheduled ?? 0;
            const predsReady = d.predictions_available === true;
            return (
              <button
                key={d.date}
                onClick={() => setSelectedDate(d.date)}
                data-testid={`ht-betslip-day-${d.date}`}
                className={`snap-start shrink-0 w-[6.5rem] sm:w-28 rounded-2xl border px-3 py-2.5 text-left transition-all ${
                  isActive
                    ? "border-amber-500 bg-amber-500/15 shadow-md shadow-amber-500/10"
                    : "border-border bg-card hover:border-amber-500/40"
                }`}
              >
                <div className={`text-[10px] uppercase tracking-[0.14em] font-bold mb-1 ${isActive ? "text-amber-300" : "text-muted-foreground"}`}>
                  {fmtDayLabel(d.date, week.today)}
                </div>
                {!isEmpty ? (
                  <>
                    <div className="flex items-baseline gap-1">
                      <span className="font-display font-extrabold text-lg tabular-nums leading-none">{d.n_picks}</span>
                      <span className="text-[10px] text-muted-foreground">picks</span>
                    </div>
                    {hitRate != null ? (
                      <div className={`text-[10px] tabular-nums font-semibold mt-1 ${
                        hitRate >= 70 ? "text-emerald-300" : hitRate >= 50 ? "text-amber-300" : "text-rose-300"
                      }`}>
                        {hitRate}% hit
                      </div>
                    ) : (
                      <div className="text-[10px] text-muted-foreground mt-1">live</div>
                    )}
                  </>
                ) : predsReady ? (
                  <>
                    <div className="font-display font-bold text-xs tabular-nums leading-tight text-amber-300/90">
                      Compile pending
                    </div>
                    <div className="text-[10px] text-muted-foreground mt-1 tabular-nums">
                      {fixtures} fixtures
                    </div>
                  </>
                ) : (
                  <>
                    <div className="font-display font-bold text-xs leading-tight text-muted-foreground">
                      Awaiting fixtures
                    </div>
                    {fixtures > 0 && (
                      <div className="text-[10px] text-muted-foreground/70 mt-1 tabular-nums">
                        {fixtures} scheduled
                      </div>
                    )}
                  </>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* WhatsApp opt-in widget */}
      <WhatsAppOptIn />

      {/* Selected date label — Iter 145ax */}
      {selectedDate && (
        <div className="flex items-center justify-between mb-3" data-testid="ht-betslip-viewing-date">
          <div className="flex items-center gap-2 text-xs">
            <CalendarDays className="w-3.5 h-3.5 text-amber-300/80" />
            <span className="font-semibold tracking-wide text-foreground/90">
              {(() => {
                try {
                  const d = new Date(selectedDate + "T00:00:00");
                  return d.toLocaleDateString(undefined, { weekday: "long", year: "numeric", month: "long", day: "numeric" });
                } catch { return selectedDate; }
              })()}
            </span>
            {selectedDate === week.today && (
              <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-amber-300/80 bg-amber-500/10 border border-amber-500/30 rounded-full px-2 py-0.5">Today</span>
            )}
            {data?.settled && (
              <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-300/80 bg-emerald-500/10 border border-emerald-500/30 rounded-full px-2 py-0.5">Settled</span>
            )}
          </div>
        </div>
      )}

      {/* Summary strip — once any picks have been graded */}
      {summary && summary.graded > 0 && (
        <div className={`relative grid gap-2 sm:gap-3 mb-6 grid-cols-3 ${(summary.void || 0) > 0 ? "sm:grid-cols-5" : "sm:grid-cols-4"}`}>
          <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-3 text-center">
            <div className="text-[10px] uppercase tracking-[0.16em] text-emerald-300 font-bold mb-0.5">Hits</div>
            <div className="font-display font-bold text-xl sm:text-2xl tabular-nums text-emerald-300">{summary.hit}</div>
          </div>
          <div className="rounded-xl border border-rose-500/30 bg-rose-500/5 p-3 text-center">
            <div className="text-[10px] uppercase tracking-[0.16em] text-rose-300 font-bold mb-0.5">Misses</div>
            <div className="font-display font-bold text-xl sm:text-2xl tabular-nums text-rose-300">{summary.miss}</div>
          </div>
          <div className="rounded-xl border border-border bg-foreground/5 p-3 text-center">
            <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-bold mb-0.5">Pending</div>
            <div className="font-display font-bold text-xl sm:text-2xl tabular-nums">{summary.pending}</div>
          </div>
          {(summary.void || 0) > 0 && (
            <div
              className="rounded-xl border border-slate-500/30 bg-slate-500/10 p-3 text-center"
              data-testid="ht-summary-void"
              title="Non-graded picks — fixture postponed/cancelled or HT score unavailable from data provider. Voids don't count toward hit-rate."
            >
              <div className="text-[10px] uppercase tracking-[0.16em] text-slate-300 font-bold mb-0.5">Void</div>
              <div className="font-display font-bold text-xl sm:text-2xl tabular-nums text-slate-300">{summary.void}</div>
            </div>
          )}
          <div className={`rounded-xl border border-amber-500/40 bg-amber-500/10 p-3 text-center ${(summary.void || 0) > 0 ? "col-span-3 sm:col-span-1" : "col-span-3 sm:col-span-1"}`}>
            <div className="text-[10px] uppercase tracking-[0.16em] text-amber-300 font-bold mb-0.5">Hit Rate</div>
            <div className="font-display font-extrabold text-xl sm:text-2xl tabular-nums text-amber-300">
              {summary.hit_rate != null ? `${summary.hit_rate}%` : "—"}
            </div>
          </div>
        </div>
      )}

      {/* Picks grid */}
      <div className="relative">
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-32 rounded-2xl border border-border bg-card/40 animate-pulse" />
            ))}
          </div>
        ) : picks.length === 0 ? (
          (() => {
            const sel = week.days.find(x => x.date === selectedDate);
            const predsReady = sel?.predictions_available === true;
            const fixtures = sel?.fixtures_scheduled ?? 0;
            // Iter 145ax — when the user picks a date OLDER than yesterday
            // (i.e. outside the 7-day strip) and we have no slate for it,
            // we never compiled one — say so explicitly instead of
            // implying a "compiles tonight" future event.
            const todayIso = week.today;
            const isPast = todayIso && selectedDate && selectedDate < todayIso && !sel;
            return (
              <div className="text-center py-12 px-4" data-testid="ht-betslip-empty">
                <Sparkles className="w-10 h-10 mx-auto mb-3 text-amber-400/60" />
                <p className="font-display font-bold text-lg mb-1">
                  {isPast
                    ? "No slate was compiled for this date"
                    : selectedDate && selectedDate !== week.today
                      ? (predsReady
                          ? "Slate compiles automatically tonight"
                          : "Predictions arrive 1 day before kickoff")
                      : "No HT picks for today yet"}
                </p>
                <p className="text-xs text-muted-foreground max-w-md mx-auto">
                  {isPast
                    ? "Historical slates are only available from the date this feature launched onward. Try a more recent date."
                    : fixtures > 0
                      ? `${fixtures} fixtures scheduled for this day. The model rebuilds the slate at 06:45 UTC on game day.`
                      : "The slate is rebuilt every morning at 06:45 UTC from that day's matches. Check back shortly."}
                </p>
              </div>
            );
          })()
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {picks.map((p, idx) => {
              const pk = p.pick || {};
              const out = outcomeChip(p.outcome);
              const OutIcon = out.Icon;
              const isLoading = analysisLoadingFid === p.fixture_id;
              const qPick = quantumLookup.get(p.fixture_id, pk.market_short);
              return (
                <article
                  key={p.fixture_id}
                  onClick={() => openAnalysis(p.fixture_id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      openAnalysis(p.fixture_id);
                    }
                  }}
                  className={`group relative overflow-hidden rounded-2xl border border-border bg-card hover:border-amber-500/60 hover:shadow-lg hover:shadow-amber-500/10 transition-all p-4 cursor-pointer focus:outline-none focus:border-amber-500 ${isLoading ? "opacity-60 pointer-events-none" : ""}`}
                  data-testid={`ht-pick-${p.fixture_id}`}
                  title="Click for full match analysis"
                >
                  {/* Rank badge */}
                  <span className="absolute top-2 right-2 inline-flex items-center justify-center h-6 min-w-6 px-1.5 rounded-full bg-foreground text-background text-[10px] font-extrabold tabular-nums">
                    #{idx + 1}
                  </span>

                  {/* League + kickoff */}
                  <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-semibold mb-2">
                    <span aria-hidden>{countryToFlag(p.league?.country)}</span>
                    <span className="truncate">{p.league?.name}</span>
                    <span className="mx-0.5">·</span>
                    <span className="tabular-nums">{fmtKick(p.kickoff)}</span>
                  </div>

                  {/* Teams */}
                  <div className="font-display font-bold text-sm sm:text-base leading-tight mb-3">
                    <div className="truncate">{p.home_team?.name}</div>
                    <div className="text-muted-foreground/80 text-xs my-0.5">vs</div>
                    <div className="truncate">{p.away_team?.name}</div>
                  </div>

                  {/* Pick + confidence */}
                  <div className="flex items-end justify-between gap-2 mb-2">
                    <div className="min-w-0">
                      <span className={`inline-block text-[10px] font-bold uppercase tracking-[0.12em] px-2 py-0.5 rounded-md border ${marketBadgeTone(pk.market_short)}`}>
                        {pk.market_short}
                      </span>
                      <div className="font-display font-bold text-base sm:text-lg mt-1 truncate">{pk.prediction}</div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-display font-extrabold text-2xl tabular-nums leading-none text-amber-300">
                        {pk.adjusted_confidence}%
                      </div>
                      <div className="text-[10px] text-muted-foreground mt-0.5 tabular-nums">
                        base {pk.base_confidence}%
                      </div>
                    </div>
                  </div>

                  {/* Intelligence notes */}
                  {(pk.intel_notes || []).length > 0 && (
                    <div className="space-y-1 mb-2">
                      {pk.intel_notes.map((note, i) => (
                        <div key={i} className="flex items-start gap-1.5 text-[10px] text-muted-foreground/90 leading-snug">
                          <ShieldCheck className="w-3 h-3 mt-0.5 shrink-0 text-amber-400/70" />
                          <span>{note}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Outcome chip */}
                  <div className="flex items-center justify-between gap-2 pt-2 border-t border-border">
                    <span
                      className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.14em] px-1.5 py-0.5 rounded-md border ${out.cls} ${p.outcome === "void" ? "cursor-help" : ""}`}
                      title={p.outcome === "void" ? (p.void_reason || "Pick not graded — see why") : undefined}
                      data-testid={`ht-pick-outcome-${p.fixture_id}`}
                    >
                      <OutIcon className="w-2.5 h-2.5" />
                      {out.text}
                    </span>
                    {p.actual_ht && (
                      <span className="text-[10px] tabular-nums font-mono text-muted-foreground">
                        HT {p.actual_ht}
                      </span>
                    )}
                  </div>
                  {/* Void reason caption — visible inline on void picks
                       so the rationale isn't hidden behind a hover. */}
                  {p.outcome === "void" && p.void_reason && (
                    <div
                      className="mt-1.5 text-[10px] italic text-slate-300/80 leading-snug flex items-start gap-1.5"
                      data-testid={`ht-pick-void-reason-${p.fixture_id}`}
                    >
                      <MinusCircle className="w-3 h-3 mt-0.5 shrink-0 text-slate-400/70" />
                      <span>Why void? {p.void_reason}.</span>
                    </div>
                  )}

                  {/* Iter 145bh — Quantum side-by-side comparison */}
                  <QuantumCompareBadge
                    q={qPick}
                    testid={`ht-pick-quantum-${p.fixture_id}`}
                  />

                  {/* Click-to-analyze affordance */}
                  <div className="mt-2.5 pt-2.5 border-t border-border/60 flex items-center justify-between gap-2 text-[10px] uppercase tracking-[0.14em] font-bold text-amber-400/80 group-hover:text-amber-300 transition-colors">
                    <span>{isLoading ? "Loading analysis…" : "View deep analysis"}</span>
                    <ChevronRight className="w-3.5 h-3.5" />
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>

      <p className="relative text-[10px] text-muted-foreground/80 italic mt-5 sm:mt-6 max-w-3xl">
        Proof, not promises — every pick on this slate is graded against the actual HT score and contributes to NDAKUBET's public Halftime track record.
      </p>

      {/* Iter 145ag — Click-to-analyze drill-down modal */}
      {analysisOpen && analysisData && (
        <MatchAnalysisModal
          analysis={analysisData}
          onClose={() => { setAnalysisOpen(false); setAnalysisData(null); }}
        />
      )}
    </section>
  );
}
